import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppToast from '../common/AppToast';
import {
  DASHBOARD_QUICK_ACTIONS_CATALOG,
  DASHBOARD_QUICK_ACTIONS_DEFAULT,
  DASHBOARD_QUICK_ACTIONS_LIMIT,
  getDashboardQuickActionById,
  normalizeDashboardQuickActionsSelection,
} from '../../constants/dashboardQuickActions';
import {
  getDashboardQuickActions,
  saveDashboardQuickActions,
} from '../../services/dashboardService';
import { useNavigation } from '../../hooks/useNavigation';

/** Identity of the scope a selection belongs to: one row per tenant and user. */
const scopeKeyOf = (user) => {
  const tenant =
    user?.tenant_id != null && user.tenant_id !== ''
      ? String(user.tenant_id)
      : user?.tenant_name || '';
  return `${tenant}:${user?.user_id ?? user?.id ?? ''}`;
};

/**
 * Personalized shortcuts on the Home dashboard.
 *
 * Two invariants drive the state handling here:
 *
 * - A selection belongs to one (tenant, user) scope. Switching tenants aborts
 *   the in-flight read, drops the current selection and reloads, so shortcuts
 *   from the previous tenant are never displayed or re-saved. There is no
 *   client-side copy of the selection for the same reason.
 * - A save is only "done" once the backend confirms it. A failure rolls the
 *   list back and says so; nothing is retried behind the user's back.
 */
function DashboardQuickActionsSection({ user }) {
  const { t } = useTranslation();
  const { getTenantNavigationTarget } = useNavigation();
  const scopeKey = scopeKeyOf(user);

  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const activeScopeRef = useRef(scopeKey);
  const activeSaveControllerRef = useRef(null);

  useEffect(() => {
    activeScopeRef.current = scopeKey;
    activeSaveControllerRef.current?.abort();
    activeSaveControllerRef.current = null;
    const controller = new AbortController();
    let current = true;

    // Reset before fetching: whatever is on screen belongs to the scope we
    // just left.
    setSelection(null);
    setLoading(true);
    setLoadFailed(false);
    setSaving(false);
    setEditorOpen(false);

    const hydrate = async () => {
      try {
        const actionIds = await getDashboardQuickActions({ signal: controller.signal });
        if (!current) return;
        // Null means "never personalized", so the catalog default applies.
        setSelection(
          Array.isArray(actionIds)
            ? normalizeDashboardQuickActionsSelection(actionIds)
            : [...DASHBOARD_QUICK_ACTIONS_DEFAULT]
        );
        setLoadFailed(false);
      } catch (error) {
        if (!current || controller.signal.aborted) return;
        console.warn('Failed to load the dashboard quick actions preference', error);
        setSelection(null);
        setLoadFailed(true);
      } finally {
        if (current) setLoading(false);
      }
    };

    hydrate();
    return () => {
      current = false;
      controller.abort();
      activeSaveControllerRef.current?.abort();
    };
    // `user` is only read for its scope; a new object identity with the same
    // scope must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, reloadToken]);

  const persist = useCallback(
    async (nextSelection) => {
      const scopeAtStart = activeScopeRef.current;
      const previous = selection;
      const optimistic = normalizeDashboardQuickActionsSelection(nextSelection);
      const controller = new AbortController();

      activeSaveControllerRef.current = controller;
      setSelection(optimistic);
      setSaving(true);
      try {
        const saved = await saveDashboardQuickActions(optimistic, {
          signal: controller.signal,
        });
        if (activeScopeRef.current !== scopeAtStart) return false;
        setSelection(normalizeDashboardQuickActionsSelection(saved));
        return true;
      } catch (error) {
        if (controller.signal.aborted || activeScopeRef.current !== scopeAtStart) {
          return false;
        }
        console.warn('Failed to save the dashboard quick actions preference', error);
        // The server has the old list, so the UI goes back to it instead of
        // pretending the change stuck.
        setSelection(previous);
        setToast({
          message: t('dashboard.quickActionsEditor.saveError'),
          severity: 'error',
        });
        return false;
      } finally {
        if (activeSaveControllerRef.current === controller) {
          activeSaveControllerRef.current = null;
          setSaving(false);
        }
      }
    },
    [selection, t]
  );

  const effectiveSelection = useMemo(
    () => (selection === null ? [] : selection),
    [selection]
  );

  // Prototype parity (PBI #3968): shortcuts are NOT filtered by permission,
  // the same rule `DashboardCommonHome` applies to its navigation rows. A
  // destination the session cannot open is bounced by the route's own
  // `ProtectedRoute` — filtering here instead collapsed the section to the
  // empty state whenever the session arrived without its permission list,
  // which is exactly what the Usuário Cliente profile hits.
  const selectedActions = useMemo(
    () => effectiveSelection.map(getDashboardQuickActionById).filter(Boolean),
    [effectiveSelection]
  );

  const availableActions = useMemo(
    () =>
      DASHBOARD_QUICK_ACTIONS_CATALOG.filter(
        (action) => !effectiveSelection.includes(action.id)
      ),
    [effectiveSelection]
  );

  const closeEditor = () => setEditorOpen(false);

  const retryLoad = () => setReloadToken((token) => token + 1);

  const removeAction = async (actionId) => {
    const ok = await persist(effectiveSelection.filter((id) => id !== actionId));
    if (ok) {
      setToast({
        message: t('dashboard.quickActionsEditor.removed'),
        severity: 'success',
      });
    }
  };

  const addAction = async (actionId) => {
    if (!actionId || effectiveSelection.includes(actionId)) {
      return;
    }
    if (effectiveSelection.length >= DASHBOARD_QUICK_ACTIONS_LIMIT) {
      setToast({
        message: t('dashboard.quickActionsEditor.limitReached'),
        severity: 'error',
      });
      return;
    }
    const ok = await persist([...effectiveSelection, actionId]);
    if (ok) {
      setToast({
        message: t('dashboard.quickActionsEditor.added'),
        severity: 'success',
      });
    }
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className="quick-actions-status" data-testid="quick-actions-loading" role="status">
          {t('dashboard.quickActionsEditor.loading')}
        </div>
      );
    }

    if (loadFailed) {
      return (
        <div className="quick-actions-status is-error" data-testid="quick-actions-error" role="alert">
          <span>{t('dashboard.quickActionsEditor.loadError')}</span>
          <button type="button" className="btn outline" onClick={retryLoad}>
            {t('dashboard.messages.retry')}
          </button>
        </div>
      );
    }

    if (selectedActions.length === 0) {
      return (
        <div className="quick-actions-status" data-testid="quick-actions-empty">
          {t('dashboard.quickActionsEditor.emptyState')}
        </div>
      );
    }

    return (
      <div className="quick-actions">
        {selectedActions.map((action) => (
          <Link
            key={action.id}
            className="btn outline"
            to={{
              ...getTenantNavigationTarget(action.route),
              state: action.id === 'new-schedule' ? { openScheduleCreate: true } : undefined,
            }}
            data-quick-action-id={action.id}
          >
            {t(action.labelKey)}
          </Link>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="section-heading">
        <h2>{t('dashboard.quickActions')}</h2>
        <button
          className="icon-btn action-icon dashboard-quick-actions-edit"
          type="button"
          aria-label={t('dashboard.quickActionsEditor.editAria')}
          onClick={() => setEditorOpen(true)}
          disabled={loading || loadFailed}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            edit
          </span>
        </button>
      </div>

      {renderList()}

      {editorOpen ? (
        <div className="modal open dashboard-quick-actions-modal" aria-hidden="false">
          <button
            type="button"
            className="modal-backdrop"
            aria-label={t('common.close')}
            onClick={closeEditor}
          />
          <div
            className="modal-dialog dashboard-quick-actions-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboardQuickActionsTitle"
            aria-describedby="dashboardQuickActionsSubtitle"
          >
            <div className="modal-header">
              <div>
                <h3 id="dashboardQuickActionsTitle">{t('dashboard.quickActionsEditor.title')}</h3>
                <p id="dashboardQuickActionsSubtitle" className="modal-subtitle">
                  {t('dashboard.quickActionsEditor.subtitle')}
                </p>
              </div>
              <button
                className="icon-btn"
                type="button"
                aria-label={t('common.close')}
                onClick={closeEditor}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  close
                </span>
              </button>
            </div>

            <div className="modal-body dashboard-quick-actions-body">
              <div className="dashboard-quick-actions-editor">
                <section className="dashboard-quick-actions-panel">
                  <div className="dashboard-quick-actions-panel-header">
                    <div>
                      <p className="dashboard-quick-actions-slot-label">
                        {t('dashboard.quickActionsEditor.selected')}
                      </p>
                      <p className="dashboard-quick-actions-panel-desc">
                        {t('dashboard.quickActionsEditor.selectedHint')}
                      </p>
                    </div>
                    {/* Counts every saved slot, including an id a past deploy
                        stored, because that is what the limit counts. */}
                    <span className="dashboard-quick-actions-current-badge">
                      {effectiveSelection.length}/{DASHBOARD_QUICK_ACTIONS_LIMIT}
                    </span>
                  </div>

                  <div className="dashboard-quick-actions-selected-list">
                    {selectedActions.length > 0 ? (
                      selectedActions.map((action) => (
                        <article
                          key={action.id}
                          className="dashboard-quick-actions-selected-item"
                          data-quick-action-id={action.id}
                        >
                          <div className="dashboard-quick-actions-selected-content">
                            <p className="dashboard-quick-actions-selected-title">
                              {t(action.labelKey)}
                            </p>
                            <p className="dashboard-quick-actions-selected-desc">
                              {t(action.descriptionKey)}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="dashboard-quick-actions-remove"
                            aria-label={t('dashboard.quickActionsEditor.removeAria', {
                              label: t(action.labelKey),
                            })}
                            onClick={() => removeAction(action.id)}
                            disabled={saving}
                          >
                            <span className="material-symbols-rounded" aria-hidden="true">
                              close
                            </span>
                          </button>
                        </article>
                      ))
                    ) : (
                      <div className="dashboard-quick-actions-empty">
                        {t('dashboard.quickActionsEditor.emptySelected')}
                      </div>
                    )}
                  </div>
                </section>

                <section className="dashboard-quick-actions-panel">
                  <div className="dashboard-quick-actions-panel-header">
                    <div>
                      <p className="dashboard-quick-actions-slot-label">
                        {t('dashboard.quickActionsEditor.available')}
                      </p>
                      <p className="dashboard-quick-actions-panel-desc">
                        {t('dashboard.quickActionsEditor.availableHint')}
                      </p>
                    </div>
                  </div>

                  <div className="dashboard-quick-actions-options">
                    {availableActions.length > 0 ? (
                      availableActions.map((action) => (
                        <button
                          key={action.id}
                          type="button"
                          className="btn outline dashboard-quick-actions-available-btn"
                          data-quick-action-id={action.id}
                          onClick={() => addAction(action.id)}
                          disabled={saving}
                        >
                          {t(action.labelKey)}
                        </button>
                      ))
                    ) : (
                      <div className="dashboard-quick-actions-empty">
                        {t('dashboard.quickActionsEditor.emptyAvailable')}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn outline" onClick={closeEditor}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AppToast toast={toast} onClose={() => setToast(null)} autoHideDuration={2200} />
    </>
  );
}

export default DashboardQuickActionsSection;
